import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { UserProfileModel } from './models/user-profile.model';
import { plainToClass } from 'class-transformer';

import { SocialAuthService, FacebookLoginProvider } from 'angularx-social-login';
import { ProfileCheckModel } from './models/profile-check.model';
import { EnvironmentSettingsModel } from './models/environment-settings.model';
import { SignUpModel } from './models/sign-up.model';
import { GoogleLoginModel } from './models/google-login.model';
import { FacebookLoginModel } from './models/facebook-login.model';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BiodigitalLoginModel } from './models/biodigital-login.model';
import { UserAppModel } from './models/user-app.model';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { PlatformLoginConfig } from './models/platform-login.model';
import { AuthPlatformEnum } from './models/auth-platform.enum';
import { UserRoleModel } from './models/user-role.model';
import { get } from 'lodash';
import { defaultUserRole, normalizeUserRole } from './models/permissions/permissions';
import { Verification } from './models/verification/verification.model';
import { HumanVerificationModel } from './models/verification/human-verification.model';
import { VerifyEmailCodeRequestModel } from './models/verification/verify-email-code-request.model';
import { VerifyEmailCodeResponseModel } from './models/verification/verify-email-code-response.model';
import { VerifyEmailRequestModel } from './models/verification/verify-email-request.model';
import { VerifyEmailResponseModel } from './models/verification/verify-email-response.model';
import { UserMessageService } from '../shared/user-message.service';
import {
  UserMessageEnum,
  UserMessageModel,
  UserProfileMessageData,
} from './models/user-message-model';
import { Store } from '@ngxs/store';
import { FacebookAuthOnline } from './state/auth/auth.actions';
import { GroupConfigModel } from './models/group-config.model';

const reCaptchaWhitelist = ['harvard.edu'];

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private store: Store,
    private socialAuthService: SocialAuthService,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private userMessageService: UserMessageService,
  ) {}

  updateSocialAuthStatus() {
    // If keep track of whether SocialAuth could initiliaze
    // This is used for Facebook login
    this.socialAuthService.initState.subscribe(
      () => {
        this.store.dispatch(new FacebookAuthOnline(true));
      },
      () => {
        this.store.dispatch(new FacebookAuthOnline(false));
      },
    );
  }

  loginWithBiodigital(login: BiodigitalLoginModel) {
    return this.http.post('/ws/user/sign/in', BiodigitalLoginModel.toJSON(login));
  }

  loginWithGoogle(login: GoogleLoginModel) {
    return this.http.post('/ws/user/sign/in/google', GoogleLoginModel.toJSON(login));
  }

  // Deprecated
  loginWithFacebook(login: FacebookLoginModel) {
    return this.http.post('/ws/user/sign/in/facebook', FacebookLoginModel.toJSON(login));
  }

  logout() {
    return this.http.get('/ws/user/sign/out');
  }

  checkProfile(email: string): Observable<ProfileCheckModel> {
    return this.http.post('/ws/user/profile/check', { version: 2, email: email }).pipe(
      map((result: any) => {
        const profileCheck = ProfileCheckModel.fromJSON(result);
        profileCheck.isThirdParty = profileCheck.thirdPartyProvider !== '';
        return profileCheck;
      }),
    );
  }

  getUserProfile(hardCheck: boolean): Observable<UserProfileModel> {
    const profileURL = hardCheck
      ? '/ws/user/profile?version=2&subscription_hard_check=1'
      : '/ws/user/profile?version=2';

    return this.http.get(profileURL).pipe(
      map((result: any) => {
        // to profile model
        const profile = plainToClass(UserProfileModel, result as Object);

        // normalize permissions
        profile.organizations.forEach((organization) => {
          // NOTE: set user role with permissions, in the future permissions will be initialized server size
          const userRole: UserRoleModel = get(organization, 'user_role', defaultUserRole);
          organization.user_role = normalizeUserRole(userRole);
        });
        return profile;
      }),
    );
  }

  // Gets a list of user roles for an organization
  getUserRoles(): Observable<UserRoleModel[]> {
    return this.http.get('/ws/organization/user/roles').pipe(
      map((result: any) => {
        return result.user_roles.map((user_role: any) => {
          const userRole: UserRoleModel = plainToClass(UserRoleModel, user_role as Object);
          return normalizeUserRole(userRole);
        });
      }),
    );
  }

  /*
    Updates user profile with partial profile fields i.e., { first_name, last_name }
  */
  updaterUserProfile(profile: Partial<UserProfileModel>): Observable<any> {
    return this.http.post('/ws/user/profile?version=2', profile);
  }

  deleteUserProfile(): Observable<string> {
    return this.http
      .post('/ws/user/profile/delete', {})
      .pipe(map((response: any) => response.deleted_user_email));
  }

  getEnvironmentSettings(): Observable<EnvironmentSettingsModel> {
    return this.http.get('/ws/environment/settings?version=2').pipe(
      map((result: any) => {
        return EnvironmentSettingsModel.fromJSON(result);
      }),
    );
  }

  /**
   * Gets a list of user apps
   */
  getUserApps(): Observable<UserAppModel[]> {
    return this.http
      .get('/ws/developer/apps/profile')
      .pipe(map((result: any) => (result && result.apps ? JSON.parse(result.apps) : [])));
  }

  getGroupConfigFromRoute(snapshot: ActivatedRouteSnapshot): GroupConfigModel {
    const group = snapshot.queryParams.group || '';
    const groupCode = snapshot.queryParams.code || '';
    const invitation = snapshot.queryParams.group_invite || '';
    const osptok = snapshot.queryParams.osptok || '';

    return {
      group: group,
      groupCode: groupCode,
      groupCodeMode: groupCode ? 1 : 0,
      groupInviteMode: invitation ? 1 : 0,
      groupInviteKey: invitation,
      groupOvidMode: osptok ? 1 : 0,
      groupOvidOsptok: osptok,
    };
  }

  getPlatformConfigFromRoute(snapshot: ActivatedRouteSnapshot): PlatformLoginConfig {
    const { zendesk_signin, return_to, brand_id, locale_id, timestamp } = snapshot.queryParams;

    // Zendesk login
    if (zendesk_signin === '1') {
      return {
        platform: AuthPlatformEnum.Zendesk,
        returnTo: return_to,
        brandId: brand_id,
        localeId: locale_id,
        timestamp: timestamp,
      };
    } else {
      return undefined;
    }
  }

  isLoggedIn() {
    // Uses version 2, which responds with 200 status for good, 401 if not
    return this.http.get('/ws/user/is/signed-in?version=2');
  }

  getProfileRestrictions(profile: UserProfileModel): {
    email: boolean;
    ip: boolean;
    organizationName: string;
  } {
    let emailRestricted = false;
    let ipRestricted = false;
    let name = '';

    profile.organizations.forEach((organization) => {
      if (emailRestricted || ipRestricted) {
        return;
      }

      name = organization.name;

      emailRestricted =
        organization.user_is_subscribed &&
        organization.email_domain_restriction &&
        !organization.user_has_email_domain_access;

      ipRestricted =
        organization.user_is_subscribed &&
        organization.ip_address_restriction &&
        !organization.user_has_domain_access;

      if (emailRestricted || ipRestricted) {
      }
    });

    return {
      organizationName: name,
      email: emailRestricted,
      ip: ipRestricted,
    };
  }

  resetPassword(email: string) {
    return this.http.post('/ws/user/password/forgot', { email: email });
  }

  updatePassword(currentPassword: string, newPassword: string, confirmNewPassword: string) {
    return this.http.post('/ws/user/password/change', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_new_password: confirmNewPassword,
    });
  }

  signUp(value: SignUpModel) {
    return this.http.post('/ws/user/sign/up', SignUpModel.toJSON(value));
  }

  authenticateWithFacebook() {
    return this.socialAuthService.signIn(FacebookLoginProvider.PROVIDER_ID);
  }

  verifyToken(token: string): Observable<Verification> {
    return this.http
      .post('/ws/recaptcha/siteverify', {
        recaptcha_reponse_token: token,
        recaptcha_version: 'v3',
      })
      .pipe(map((response: Verification) => response));
  }

  /**
   * Google returns a score between 0 and 1 rating the likelihood that a user is human
   * Real users typically rank at 0.9+
   * The default acceptance level is 0.5, but we're starting at 0.2 and will adjust after
   * monitoring the admin dashboard at: https://www.google.com/recaptcha/admin
   *
   * https://developers.google.com/recaptcha/docs/v3
   */
  isHuman(action: string): Observable<HumanVerificationModel> {
    return this.reCaptchaV3Service.execute(action).pipe(
      mergeMap((token: string) => this.verifyToken(token)),
      map((verification: Verification) => {
        const isHuman = verification.action === action && verification.score > 0.2;
        return {
          isHuman,
          verification,
        };
      }),
    );
  }

  isReCaptchaWhiteListed(email: string): boolean {
    return reCaptchaWhitelist.some((entry) => {
      return new RegExp(`${entry}$`, 'i').test(email);
    });
  }

  requestVerificationCode(
    request: VerifyEmailCodeRequestModel,
  ): Observable<VerifyEmailCodeResponseModel> {
    return this.http
      .post('/ws/user/verification/request', request)
      .pipe(map((result) => plainToClass(VerifyEmailCodeResponseModel, result as Object)));
  }

  verifyProfile(verification: VerifyEmailRequestModel): Observable<VerifyEmailResponseModel> {
    return this.http
      .post('/ws/user/verification/confirm', verification)
      .pipe(map((result) => plainToClass(VerifyEmailResponseModel, result as Object)));
  }

  // check if user profile not completed (i.e., segmentation data complete)
  checkProfileIncomplete(): Observable<UserMessageModel<UserProfileMessageData>> {
    return this.userMessageService
      .getUserMessage(UserMessageEnum.CompleteProfileAlert, 'v2', false)
      .pipe(
        catchError(() =>
          of({
            messageType: UserMessageEnum.CompleteProfileAlert,
            messageData: <any>{},
            showUserAlert: false,
          }),
        ),
      );
  }
}
